import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, DollarSign, Clock, Users, ArrowRight, Briefcase } from 'lucide-react';
import Footer from '@/components/Footer';
import PublicNav from '@/components/PublicNav';

const AvailableJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchAvailableJobs();
  }, []);

  const fetchAvailableJobs = async (pageNum = 1) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/available-jobs?page=${pageNum}&limit=12`
      );
      
      if (response.data.success) {
        const newJobs = response.data.jobs || [];
        
        if (pageNum === 1) {
          setJobs(newJobs);
        } else {
          setJobs(prev => [...prev, ...newJobs]);
        }
        
        setHasMore(newJobs.length === 12);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch available jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(0)}`;
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return 'No description available';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getCrewPayment = (job) => {
    // Calculate crew payment (98% of job total after platform fee)
    let total = job.budget || job.total_amount || 0;

    // For jobs without budget, calculate from crew members or hours/rate
    if (total === 0) {
      if (job.crew_members && job.crew_members.length > 0) {
        // Advanced job: sum all crew member payments
        total = job.crew_members.reduce((sum, member) => {
          return sum + ((member.hours || 0) * (member.ratePerHour || 0));
        }, 0);
      } else if (job.hours && job.pay_rate) {
        // Simple job: hours × rate
        total = (job.hours || 0) * (job.pay_rate || 0);
      }
    }

    return total * 0.98; // Crew gets 98%, platform keeps 2%
  };

  const getJobHours = (job) => {
    // Get hours from job, or calculate from crew_members if not set
    if (job.hours && job.hours > 0) return job.hours;

    // Try to get hours from crew_members
    if (job.crew_members && job.crew_members.length > 0) {
      return job.crew_members.reduce((sum, member) => sum + (member.hours || 0), 0);
    }

    return 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0A12] via-[#1a1625] to-[#0B0A12]">
      {/* Navigation */}
      <PublicNav />

      {/* Hero Section - pt-20 accounts for fixed nav */}
      <section className="relative pt-28 sm:pt-40 pb-12 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <Badge className="bg-[#7B3FF2]/20 text-[#7DD3C0] border-[#7DD3C0]/30 px-4 py-2 text-sm font-semibold mb-6">
              Live Job Opportunities
            </Badge>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Exo, sans-serif' }}>
            Available Jobs
          </h1>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Discover photography and videography opportunities across Australia. Join thousands of crew members earning with Shootzu.
          </p>
          
          <a href="https://shootzu.com/dashboard/" target="_blank" rel="noopener noreferrer">
            <Button className="bg-gradient-to-r from-[#7B3FF2] to-[#7DD3C0] hover:from-[#6930d1] hover:to-[#5bb3a0] text-white px-10 py-6 text-lg font-bold rounded-xl shadow-2xl">
              Join as Crew
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Jobs Grid */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B3FF2]"></div>
              <p className="mt-4 text-gray-400">Loading opportunities...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20">
              <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-xl font-semibold text-gray-400">No jobs available at the moment</p>
              <p className="text-gray-500 mt-2">Check back soon for new opportunities!</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {jobs.map((job) => (
                  <Card key={job.id} className="bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#7B3FF2]/50 transition-all duration-300 hover:shadow-2xl hover:shadow-[#7B3FF2]/20 group">
                    <CardContent className="p-6">
                      {/* Job Header */}
                      <div className="flex items-start justify-between mb-4">
                        <Badge className="bg-[#7DD3C0]/20 text-[#7DD3C0] border-[#7DD3C0]/30 px-3 py-1 text-xs font-bold">
                          AVAILABLE
                        </Badge>
                        <div className="text-right">
                          <p className="text-2xl font-black text-[#7DD3C0]">
                            {formatCurrency(getCrewPayment(job))}
                          </p>
                          <p className="text-xs text-gray-400">Crew Payment</p>
                        </div>
                      </div>

                      {/* Job Title */}
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#7DD3C0] transition-colors">
                        {job.title}
                      </h3>

                      {/* Job Details */}
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-[#7B3FF2]" />
                          <span className="text-gray-300 text-sm">
                            {formatDate(job.date)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#7DD3C0]" />
                          <span className="text-gray-300 text-sm truncate">
                            {job.location || job.location_address}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-yellow-400" />
                          <span className="text-gray-300 text-sm">
                            {job.crew_count > 1 ? `${job.crew_count} crew positions` : `${getJobHours(job)} hours`}
                          </span>
                        </div>
                      </div>

                      {/* Job Brief Preview */}
                      <div className="mb-4">
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {truncateText(job.description || job.brief)}
                        </p>
                      </div>

                      {/* Job Owner */}
                      <div className="flex items-center gap-3 mb-6 p-3 bg-white/5 rounded-lg border border-white/10">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] flex items-center justify-center overflow-hidden">
                          {job.client_photo ? (
                            <img src={job.client_photo} alt={job.client_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-bold text-sm">
                              {(job.client_name || 'Job Owner').charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">
                            Posted by
                          </p>
                          <p className="text-white font-semibold text-sm">
                            {(job.client_name || 'Job Owner').split(' ')[0]} {/* Show first name only */}
                          </p>
                        </div>
                      </div>

                      {/* Apply Button */}
                      <a href="https://shootzu.com/dashboard/" target="_blank" rel="noopener noreferrer" className="block">
                        <Button className="w-full bg-gradient-to-r from-[#7B3FF2] to-[#7DD3C0] hover:from-[#6930d1] hover:to-[#5bb3a0] text-white font-bold py-3 rounded-xl transition-all group-hover:scale-105">
                          Apply for Job
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <div className="text-center mt-12">
                  <Button
                    onClick={() => fetchAvailableJobs(page + 1)}
                    variant="outline"
                    className="border-[#7B3FF2] text-[#7B3FF2] hover:bg-[#7B3FF2] hover:text-white px-8 py-4 text-lg font-semibold"
                  >
                    Load More Opportunities
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AvailableJobs;